/**
 * Returns HTTP headers describing the client for API request logging and support.
 * Safe to call in any environment (returns empty/minimal headers if navigator is unavailable).
 * Use with both Amplify API (custom_header) and api-interceptor fetch() so all requests
 * carry the same client metadata without changing call sites.
 */
export function getClientMetadataHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  try {
    if (typeof navigator === "undefined") return headers;

    // User-Agent: browsers forbid JS from setting the User-Agent header, so we send it
    // in a custom header the backend can rely on. API Gateway still forwards the real UA.
    const ua = navigator.userAgent || "";
    if (ua) headers["X-Client-User-Agent"] = ua;

    // Platform: always "web" for this app; mobile apps would send their own
    headers["X-Client-Platform"] = "web";

    // Device: desktop vs mobile/tablet (heuristic from UA and touch)
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
      (typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 0);
    headers["X-Client-Device"] = isMobile ? "mobile" : "desktop";

    // OS and version (best-effort from userAgent)
    const osMatch = ua.match(
      /(Windows NT [\d.]+)|(Mac OS X [\d_.]+)|(Android [\d.]+)|(iPhone OS [\d_]+)|(iPad.*OS [\d_]+)/i
    );
    const os = osMatch ? osMatch[0].replace(/_/g, ".") : navigator.platform || "unknown";
    headers["X-Client-OS"] = os;
    // Optional: separate version if we parsed it (e.g. "10" from "Windows NT 10.0")
    const versionMatch = ua.match(/(?:Windows NT|Mac OS X|Android|OS) ([\d.]+)/i);
    if (versionMatch) headers["X-Client-OS-Version"] = versionMatch[1];

    // Timezone (e.g. "America/New_York")
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) headers["X-Client-Timezone"] = tz;
    } catch {
      // ignore
    }

    // App type: identify this client (set REACT_APP_CLIENT_APP_TYPE at build time or default)
    headers["X-Client-App-Type"] = process.env.REACT_APP_CLIENT_APP_TYPE || "simpl-api";

    // App version (set REACT_APP_VERSION at build time if desired; REACT_APP_ENV as fallback)
    const appVersion =
      process.env.REACT_APP_VERSION || process.env.REACT_APP_ENV || "";
    if (appVersion) headers["X-Client-App-Version"] = appVersion;
  } catch {
    // Never throw: return whatever we have so existing behavior is unchanged
  }

  return headers;
}
